// Toast 功能將在 Hook 中處理

export interface FrontendError {
    message: string
    code?: string
    details?: any
    timestamp: string
    severity: 'low' | 'medium' | 'high' | 'critical'
}

export class FrontendErrorService {
    /**
     * 記錄前端錯誤
     */
    static logError(error: Error | any, context?: any) {
        const errorLog = {
            error: {
                name: error.name || 'UnknownError',
                message: error.message || 'Unknown error occurred',
                stack: error.stack,
            },
            context: {
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                ...context,
            },
            severity: this.getErrorSeverity(error),
        }

        // 記錄到控制台
        console.error('[FRONTEND_ERROR]', errorLog)

        // TODO: 在生產環境中發送到監控系統
        // 例如：Sentry, LogRocket 等

        return errorLog
    }

    /**
     * 處理 API 錯誤響應
     */
    static handleApiError(error: any): FrontendError {
        let frontendError: FrontendError = {
            message: '發生未知錯誤，請稍後再試',
            severity: 'medium',
            timestamp: new Date().toISOString(),
        }

        if (error.response) {
            // API 返回的錯誤
            const apiError = error.response.data
            frontendError = {
                message: apiError.message || apiError.error || '伺服器錯誤',
                code: apiError.code,
                details: apiError.details,
                severity: this.getApiErrorSeverity(error.response.status),
                timestamp: apiError.timestamp || new Date().toISOString(),
            }
        } else if (error.request) {
            // 網路錯誤
            frontendError = {
                message: '網路連接失敗，請檢查網路連接',
                code: 'NETWORK_ERROR',
                severity: 'high',
                timestamp: new Date().toISOString(),
            }
        } else if (error.code === 'ECONNABORTED') {
            // 請求超時
            frontendError = {
                message: '請求超時，請稍後再試',
                code: 'TIMEOUT',
                severity: 'medium',
                timestamp: new Date().toISOString(),
            }
        }

        return frontendError
    }

    /**
     * 顯示錯誤 Toast
     */
    static showErrorToast(error: FrontendError | string, toastFn?: any) {
        const message = typeof error === 'string' ? error : error.message
        const severity = typeof error === 'string' ? 'medium' : error.severity

        const toastConfig = {
            title: '錯誤',
            description: message,
            status: 'error' as const,
            duration: this.getToastDuration(severity),
            isClosable: true,
            position: 'top-right' as const,
        }

        if (toastFn) {
            toastFn(toastConfig)
        } else {
            console.error('Toast function not available:', message)
        }
    }

    /**
     * 顯示成功 Toast
     */
    static showSuccessToast(message: string, toastFn?: any) {
        const toastConfig = {
            title: '成功',
            description: message,
            status: 'success' as const,
            duration: 3000,
            isClosable: true,
            position: 'top-right' as const,
        }

        if (toastFn) {
            toastFn(toastConfig)
        } else {
            console.log('Success:', message)
        }
    }

    /**
     * 處理表單驗證錯誤
     */
    static handleFormValidationError(errors: any): string[] {
        const errorMessages: string[] = []

        if (typeof errors === 'object' && errors !== null) {
            Object.entries(errors).forEach(([, error]: [string, any]) => {
                if (Array.isArray(error)) {
                    errorMessages.push(...error)
                } else if (typeof error === 'string') {
                    errorMessages.push(error)
                } else if (error.message) {
                    errorMessages.push(error.message)
                }
            })
        }

        return errorMessages
    }

    /**
     * 判斷錯誤嚴重程度
     */
    private static getErrorSeverity(error: any): 'low' | 'medium' | 'high' | 'critical' {
        if (error.name === 'ValidationError') return 'low'
        if (error.name === 'NetworkError') return 'high'
        if (error.name === 'TimeoutError') return 'medium'
        if (error.message?.includes('unauthorized')) return 'medium'
        if (error.message?.includes('forbidden')) return 'high'

        return 'medium'
    }

    /**
     * 根據 HTTP 狀態碼判斷嚴重程度
     */
    private static getApiErrorSeverity(statusCode: number): 'low' | 'medium' | 'high' | 'critical' {
        if (statusCode >= 400 && statusCode < 500) return 'medium' // 客戶端錯誤
        if (statusCode >= 500) return 'high' // 伺服器錯誤
        return 'low'
    }

    /**
     * 根據嚴重程度決定 Toast 顯示時間
     */
    private static getToastDuration(severity: 'low' | 'medium' | 'high' | 'critical'): number {
        switch (severity) {
            case 'low': return 3000
            case 'medium': return 5000
            case 'high': return 8000
            case 'critical': return 10000
            default: return 5000
        }
    }
}